import React, { useContext, useRef, useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import Tooltip from '@components/Tooltip'
import Text from '@components/Text'
import { COLORS } from '@helpers/constants'
import { AppContext } from '@state/context'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { increaseShownTooltipCount } from '@state/actions/appActions'
import { debounce } from 'lodash'

const withTooltip = (Component) => {
  const WrappedComponent = React.forwardRef((props, componentRef) => {
    const [{ app: { shownTooltips } }, dispatch] = useContext(AppContext)
    const { tooltip, tooltipProps } = props
    const { currentUser } = useCurrentUser()
    const [showTooltip, setShowTooltip] = useState(false)
    const ref = useRef()
    const debouncedIncreaseShownTooltips = useMemo(
      () => debounce(() => dispatch(increaseShownTooltipCount()), 10000, { trailing: true }),
      []
    )

    useEffect(() => {
      if (showTooltip && shownTooltips < 4) {
        debouncedIncreaseShownTooltips()
      }

      if (ref.current && showTooltip) {
        const text = ref.current.querySelector('.tooltip-text-element')
        const popperElement = ref.current.querySelector('.popper-element')

        const { width } = text.getBoundingClientRect()
        if (width < 180) {
          popperElement.style.minWidth = `unset`
        } else {
          popperElement.style.width = `calc(${width}px + 2rem)`
        }
      }
    }, [showTooltip])

    if (!currentUser?.showTooltips || !tooltip) return <Component {...props} ref={componentRef} />

    return (
      <div ref={ref} className={'block'} onMouseOver={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
        <Component {...props} ref={componentRef} />
        <Tooltip
          {...tooltipProps}
          color={COLORS.LIGHT_RED}
          show={showTooltip}
          referenceElement={ref.current}
          maxWidth={'300px'}
        >
          <Text extraClasses='tooltip-text-element inline mx-auto' color={COLORS.BLACK.TEXT}>{tooltip}</Text>
        </Tooltip>
      </div>
    )
  })

  WrappedComponent.propTypes = {
    ...Component?.propTypes,
    tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }

  return WrappedComponent
}

export default withTooltip
